import { Grid, Tab, Tabs } from "@mui/material";
import { useFormik } from "formik";
import React, { useEffect, useMemo, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import * as Yup from "yup";
import {
  ApiError,
  ApiErrorClass,
  Profile as ProfileModel,
} from "@readyfit-common/models";
import AthletesLayout from "src/grading/layout/AthletesLayout/AthletesLayout";
import { useAppDispatch, useAppSelector } from "src/store/hooks";
import {
  useGetUserQuery,
  useUpdateProfileImageMutation,
  useUpdateProfileMutation,
} from "src/store/reducers/auth/authApiSlice";
import { selectCurrentUser } from "src/store/reducers/auth/authSlice";
import { useUpdateCoachImagesMutation } from "src/store/reducers/documents/documentsApiSlice";
import { showSnackbar } from "src/store/reducers/navigation/navigationSlice";
import AthleteCard from "../Athletes/AthleteCard/AthleteCard";
import useStyles from "./Profile.styles";
import ProfileTab from "./ProfileTab";
import SettingsTab from "./SettingsTab";

const Profile = () => {
  const [isEditing, setIsEditing] = useState(false);
  const classes = useStyles({ isEditing })();
  const profile: ProfileModel = useAppSelector(selectCurrentUser);
  const [tabValue, setTabValue] = useState(0);
  const [profilePhoto, setProfilePhoto] = useState(
    profile?.profileImageUrl || "",
  );
  const [backgroundPhoto, setBackgroundPhoto] = useState(
    profile?.coachInfo?.images?.[0] || "",
  );
  const [updateProfileMutation, { isLoading: isLoadingUpdateProfile }] =
    useUpdateProfileMutation();
  const [
    updateProfileImageMutation,
    { isLoading: _isLoadingUpdateProfileImage },
  ] = useUpdateProfileImageMutation();
  const [
    updateCoachImagesMutation,
    { isLoading: _isLoadingUpdateCoachImages },
  ] = useUpdateCoachImagesMutation();
  const dispatch = useAppDispatch();
  const canvasRef = useRef<SignatureCanvas>(null);
  useGetUserQuery();
  const [showExampleModal, setShowExampleModal] = useState(false);

  const profileInfoRows = useMemo(
    () => [
      {
        label: "Full name",
        value: `${profile?.firstName} ${profile?.lastName}`,
        field: "fullName",
        fieldType: "text",
      },
      {
        label: "Phone number",
        value: profile?.phoneNumber || "-",
        field: "phoneNumber",
        fieldType: "phoneNumber",
      },
      {
        label: "Email",
        value: profile?.email,
        field: "email",
        fieldType: "text",
      },
      {
        label: "Specialties",
        value: profile?.coachInfo?.specialties?.join(", "),
        field: "specialties",
        fieldType: "list",
      },
      {
        label: "Coach Bio",
        value: profile?.coachInfo?.aboutMe,
        field: "aboutMe",
        fieldType: "textarea",
      },
    ],
    [profile],
  );

  useEffect(() => {
    setProfilePhoto(profile?.profileImageUrl || "");
    setBackgroundPhoto(profile?.coachInfo?.images?.[0] || "");
  }, [profile]);

  const initialValues = profileInfoRows.reduce(
    (acc, row) => {
      acc[row.field] = row.value || "";
      return acc;
    },
    {} as { [key: string]: string },
  );

  const validationSchema = Yup.object().shape({
    fullName: Yup.string().required("Full name is required"),
    phoneNumber: Yup.string(),
    email: Yup.string().required("Email is required").email("Invalid email"),
    specialties: Yup.string()
      .required("Specialties are required")
      .test(
        "is-comma-delimited",
        "Please enter at least one specialty",
        (value) => (value ? value.trim().length > 0 : false),
      ),
    aboutMe: Yup.string().required("Coach bio is required"),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        const parsedValues = {
          firstName: values.fullName.split(" ")[0],
          lastName: values.fullName.split(" ")[1],
          phoneNumber: values.phoneNumber,
          coachInfo: {
            specialties: values.specialties.split(",").map((s) => s.trim()),
            aboutMe: values.aboutMe,
          },
        };
        const response = await updateProfileMutation(parsedValues).unwrap();

        if (!response.success)
          throw new ApiErrorClass({
            message: "Error updating profile",
          });

        dispatch(
          showSnackbar({
            open: true,
            message: "Profile updated successfully",
            type: "success",
          }),
        );
        setIsEditing(false);
      } catch (error: unknown) {
        const typedError = error as ApiError;
        dispatch(
          showSnackbar({
            open: true,
            message: typedError.data?.message || "Error updating profile",
            type: "error",
          }),
        );
      }
    },
  });

  const { values, handleChange, handleSubmit, errors } = formik;

  const handleOnProfilePhotoSelect = async (file: File) => {
    try {
      console.log("onProfilePhotoSelect file:", file);
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        setProfilePhoto(reader.result as string);
      };

      console.log("file", file);

      const formData = new FormData();

      formData.append("image", file);

      const response = await updateProfileImageMutation(formData).unwrap();

      if (!response.success)
        throw new ApiErrorClass({
          message: "Error updating profile",
        });

      dispatch(
        showSnackbar({
          open: true,
          message: response.message,
          type: "success",
        }),
      );
    } catch (error: unknown) {
      const typedError = error as ApiError;
      dispatch(
        showSnackbar({
          open: true,
          message: typedError.data?.message || "Error updating profile",
          type: "error",
        }),
      );
    }
  };

  const handleOnBackgroundPhotoSelect = async (file: File) => {
    try {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        setBackgroundPhoto(reader.result as string);
      };

      const formData = new FormData();

      formData.append("images", file);

      const response = await updateCoachImagesMutation(formData).unwrap();

      if (!response.success)
        throw new ApiErrorClass({
          message: "Error updating profile",
        });

      dispatch(
        showSnackbar({
          open: true,
          message: response.message,
          type: "success",
        }),
      );
    } catch (error: unknown) {
      const typedError = error as ApiError;
      dispatch(
        showSnackbar({
          open: true,
          message: typedError.data?.message || "Error updating profile",
          type: "error",
        }),
      );
    }
  };

  const handleEditClick = () => {
    if (!isEditing) {
      setIsEditing(true);
    } else {
      handleSubmit();
    }
  };

  return (
    <AthletesLayout>
      <Grid container className={classes.container}>
        {/* HEADER */}
        <Grid container className={classes.headerContainer}>
          <Grid item>
            <AthleteCard
              user={profile}
              profileIconStyles={classes.profileIcon}
              nameStyles={classes.name}
            />
          </Grid>

          <Grid item>
            <Tabs
              value={tabValue}
              onChange={(event, newValue) => setTabValue(newValue)}
              className={classes.tabs}
              TabIndicatorProps={{
                style: { display: "none" },
              }}
            >
              <Tab className={classes.tab} label="Profile" />
              <Tab className={classes.tab} label="Settings" />
            </Tabs>
          </Grid>
        </Grid>

        <Grid container>
          {tabValue === 0 && (
            <ProfileTab
              setIsEditing={setIsEditing}
              isEditing={isEditing}
              isLoadingUpdateProfile={isLoadingUpdateProfile}
              profilePhoto={profilePhoto}
              backgroundPhoto={backgroundPhoto}
              handleOnProfilePhotoSelect={handleOnProfilePhotoSelect}
              handleOnBackgroundPhotoSelect={handleOnBackgroundPhotoSelect}
              profileInfoRows={profileInfoRows}
              values={values}
              errors={errors}
              handleEditClick={handleEditClick}
              handleChange={handleChange as any}
              canvasRef={canvasRef}
              setShowExampleModal={setShowExampleModal}
              showExampleModal={showExampleModal}
            />
          )}

          {tabValue === 1 && <SettingsTab isEditing={isEditing} />}
        </Grid>
      </Grid>
    </AthletesLayout>
  );
};

export default Profile;
